import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { LogFilter } from '../../../../types/ApplicationLog';
import logApi from '../../../../services/logApi';

interface LogFilterPanelProps {
  filter: LogFilter;
  onFilterChange: (filter: Partial<LogFilter>) => void;
  /** Fritekstsøk — slått sammen på samme linje som filtrene for å spare topplass. */
  searchTerm: string;
  onSearchTermChange: (value: string) => void;
  onSearch: () => void;
}

const LogFilterPanel: React.FC<LogFilterPanelProps> = ({
  filter,
  onFilterChange,
  searchTerm,
  onSearchTermChange,
  onSearch,
}) => {
  const { t } = useTranslation();
  const [levels, setLevels] = useState<string[]>([]);
  // Bumpes ved Nullstill for å remounte (og dermed tømme) de ukontrollerte dato-feltene
  const [resetKey, setResetKey] = useState(0);
  const [localFilter, setLocalFilter] = useState<Partial<LogFilter>>({
    level: filter.level,
    fromDate: filter.fromDate,
    toDate: filter.toDate,
    operatorId: filter.operatorId,
  });

  useEffect(() => {
    fetchLevels();
  }, []);

  const fetchLevels = async () => {
    try {
      const availableLevels = await logApi.getLevels();
      setLevels(availableLevels);
    } catch (error) {
      console.error('Error fetching levels:', error);
    }
  };

  const handleApply = () => {
    onFilterChange(localFilter);
  };

  const handleReset = () => {
    setLocalFilter({});
    setResetKey((k) => k + 1); // remounter dato-feltene så de tømmes
    onFilterChange({
      level: undefined,
      fromDate: undefined,
      toDate: undefined,
      operatorId: undefined,
    });
  };

  const inputClass =
    'px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent';
  const labelClass = 'text-xs text-gray-500 mb-0.5';

  return (
    // Alltid synlig, kompakt: søk + filtre på én linje
    <div className="bg-gray-50 border-b border-gray-200 px-6 py-2">
      <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
        {/* Søk (først) */}
        <div className="flex flex-col min-w-[240px]">
          <label className={labelClass}>{t('common.buttons.search', {}, 'Søk')}</label>
          <div className="flex gap-1">
            <div className="relative flex-1">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => onSearchTermChange(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onSearch()}
                placeholder={t('warehouse.modules.logs.searchPlaceholder')}
                className={`${inputClass} w-full pl-7`}
              />
              <MagnifyingGlassIcon className="absolute left-2 top-1.5 h-4 w-4 text-gray-400" />
            </div>
            <button
              onClick={onSearch}
              title={t('common.buttons.search', {}, 'Søk')}
              className="px-2 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              <MagnifyingGlassIcon className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Nivå */}
        <div className="flex flex-col">
          <label className={labelClass}>{t('warehouse.modules.logs.level')}</label>
          <select
            value={localFilter.level || ''}
            onChange={(e) => setLocalFilter({ ...localFilter, level: e.target.value || undefined })}
            className={inputClass}
          >
            <option value="">{t('warehouse.modules.logs.allLevels')}</option>
            {levels.map((level) => (
              <option key={level} value={level}>
                {level}
              </option>
            ))}
          </select>
        </div>

        {/* Fra dato */}
        <div className="flex flex-col">
          <label className={labelClass}>{t('warehouse.modules.logs.fromDate')}</label>
          <input
            key={`from-${resetKey}`}
            type="datetime-local"
            lang="nb-NO"
            defaultValue={localFilter.fromDate || ''}
            onChange={(e) => setLocalFilter({ ...localFilter, fromDate: e.target.value || undefined })}
            className={inputClass}
          />
        </div>

        {/* Til dato */}
        <div className="flex flex-col">
          <label className={labelClass}>{t('warehouse.modules.logs.toDate')}</label>
          <input
            key={`to-${resetKey}`}
            type="datetime-local"
            lang="nb-NO"
            defaultValue={localFilter.toDate || ''}
            onChange={(e) => setLocalFilter({ ...localFilter, toDate: e.target.value || undefined })}
            className={inputClass}
          />
        </div>

        {/* Operatør-ID */}
        <div className="flex flex-col">
          <label className={labelClass}>{t('warehouse.modules.logs.operatorId')}</label>
          <input
            type="text"
            value={localFilter.operatorId || ''}
            onChange={(e) => setLocalFilter({ ...localFilter, operatorId: e.target.value || undefined })}
            onKeyDown={(e) => e.key === 'Enter' && handleApply()}
            placeholder={t('warehouse.modules.logs.operatorIdPlaceholder')}
            className={inputClass}
          />
        </div>

        {/* Knapper */}
        <div className="flex items-center gap-2 ml-auto">
          <button
            onClick={handleReset}
            className="px-3 py-1 text-sm text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
          >
            {t('common.buttons.reset')}
          </button>
          <button
            onClick={handleApply}
            className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            {t('warehouse.modules.logs.applyFilter')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LogFilterPanel;
